import Peer, { DataConnection } from 'peerjs';

export interface NetworkState {
  position?: { x: number; y: number; z: number };
  rotation?: { x: number; y: number; z: number; w: number };
  animation?: string;
}

export interface NetworkMessage {
  type: string;
  payload: any;
}

// Unambiguous characters (no 0/O, 1/I/L)
const ID_CHARS = 'BCDFGHJKMNPQRSTVWXYZ2345679';

/**
 * Generate a short, readable room code.
 * 6 chars from a 27-char alphabet → ~387M combinations.
 */
function generateRoomCode(length: number = 6): string {
  let code = '';
  for (let i = 0; i < length; i++) {
    code += ID_CHARS[Math.floor(Math.random() * ID_CHARS.length)];
  }
  return code;
}

export class NetworkService {
  private peer: Peer | null = null;
  public connection: DataConnection | null = null;

  public peerId: string | null = null;
  /** The short room code used for this session */
  public roomCode: string | null = null;
  public isHost: boolean = false;

  private messageHandlers: { [type: string]: ((payload: any) => void)[] } = {};

  constructor() {}

  public async hostGame(): Promise<string> {
    this.isHost = true;
    const roomCode = generateRoomCode();

    return new Promise((resolve, reject) => {
      this.peer = new Peer(roomCode);
      
      this.peer.on('open', (id) => {
        this.peerId = id;
        this.roomCode = roomCode;
        console.log('[Network] Host started. Room Code:', roomCode);
        resolve(roomCode);
      });

      this.peer.on('connection', (conn) => {
        console.log('[Network] Client connected');
        this.connection = conn;
        this.setupConnection();
      });

      this.peer.on('error', (err) => {
        console.error('[Network] Host error:', err);
        reject(err);
      });
    });
  }

  public async joinGame(hostId: string): Promise<void> {
    this.isHost = false;
    return new Promise((resolve, reject) => {
      this.peer = new Peer();
      
      this.peer.on('open', (id) => {
        this.peerId = id;
        console.log('[Network] Client started. Connecting to:', hostId);
        
        this.connection = this.peer!.connect(hostId, {
          reliable: true,
        });
        
        this.connection.on('open', () => {
          console.log('[Network] Connected to host!');
          this.setupConnection();
          resolve();
        });

        this.connection.on('error', (err) => {
          console.error('[Network] Connection error:', err);
          reject(err);
        });
      });

      this.peer.on('error', (err) => {
        console.error('[Network] Join error:', err);
        reject(err);
      });
    });
  }

  private setupConnection() {
    if (!this.connection) return;
    
    this.connection.on('data', (data: any) => {
      if (data && data.type) {
        this.handleMessage(data.type, data.payload);
      }
    });

    this.connection.on('close', () => {
      console.log('[Network] Connection closed');
      this.connection = null;
      this.handleMessage('PLAYER_DISCONNECTED', null);
    });
  }

  public sendMessage(type: string, payload: any) {
    if (this.connection && this.connection.open) {
      this.connection.send({ type, payload });
    }
  }

  public onMessage(type: string, callback: (payload: any) => void) {
    if (!this.messageHandlers[type]) {
      this.messageHandlers[type] = [];
    }
    this.messageHandlers[type].push(callback);
  }

  public removeMessageHandler(type: string, callback: (payload: any) => void) {
    if (this.messageHandlers[type]) {
      this.messageHandlers[type] = this.messageHandlers[type].filter(cb => cb !== callback);
    }
  }

  private handleMessage(type: string, payload: any) {
    if (this.messageHandlers[type]) {
      this.messageHandlers[type].forEach(cb => cb(payload));
    }
  }

  public isConnected(): boolean {
    return this.connection !== null && this.connection.open;
  }

  public disconnect() {
    if (this.connection) {
      this.connection.close();
      this.connection = null;
    }
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    this.peerId = null;
    this.roomCode = null;
  }
}

